import { AccountVerification, Modal, UI, Utils as UtilsModule } from 'common';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import React from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import AddSeatsModal from './AddSeatsModal.component';
import MultiUsersEditView from './MultiUsersEditView.component';
import ResendInviteModal from './ResendInviteModal.component';
import ResetPasswordModal from './ResetPasswordModal.component';
import SingleUserEditView from './SingleUserEditView.component';
import UserActionType from './UserActionType.enum';
import UserLimitReachedModal from './UserLimitReachedModal.component';
import Utils from './Utils';

const { WrappedButton, WrappedButtonType, SaveConfirmation } = UI;
const {
	BillingPlans,
	Logger,
	Session,
	Util,
	withLayoutAndModalContexts,
	UserRole,
} = UtilsModule;
const { AccountVerificationModal } = AccountVerification;
const { ModalButtonActionType } = Modal;

class UserDetails extends React.Component {
	constructor(props) {
		super(props);
		const { action, selectedUsers, enterpriseLocation } = props;
		const defaultMeterNo =
			isObject(enterpriseLocation) && isObject(enterpriseLocation.MeterAccount)
				? enterpriseLocation.MeterAccount.MeterNo
				: null;
		const { userId, customerId } = Session.getAccountInfo();
		const userRole = Session.getUserRole();
		const formData = Utils.getFormDataByAction(
			action,
			selectedUsers,
			defaultMeterNo
		); // pre-select location when adding a user on Location page
		this.isEnterpriseAccount = Utils.checkIsEnterpriseAdmin(userRole);
		const disableAllField = Utils.disableAllField(selectedUsers); // Prevents Region Admins from editing Corp Admin users

		this.state = {
			userRole,
			userID: userId,

			// enterpriseLocation exists when users open `Manage Users` tab from LocationSettingsModal component
			customerID: isObject(enterpriseLocation)
				? enterpriseLocation.CustId
				: customerId,
			selectedUsers,
			formData,
			errorMessage: {},
			fieldsChanged: formData,
			clean: true,
			disableAllField,
		};
		this.clipboard = null;
	}

	static getDerivedStateFromProps(props, state) {
		if (props.selectedUsers !== state.selectedUsers) {
			const { action, selectedUsers, regions } = props;
			const formData = Utils.getFormDataByAction(
				action,
				selectedUsers,
				regions
			);
			const disableAllField = Utils.disableAllField(selectedUsers);
			return {
				selectedUsers,
				formData,
				errorMessage: {},
				fieldsChanged: formData,
				clean: true,
				resendInviteStatus: '',
				disableAllField,
			};
		}
		return null;
	}

	handleFormInputChange = (fieldName, value) => {
		const { formData } = this.state;
		const newFormData = { ...formData };

		if (fieldName === 'phone') {
			const formattedPhone = Util.formatPhoneNumber(value);
			newFormData[fieldName] = formattedPhone;
		} else {
			newFormData[fieldName] = value;
		}
		if (fieldName === 'perPrint') {
			// Per WEBAPPS-25969, when per-print is the max value DB allowed, system treats the Per-Print checkbox unchecked.
			// And when user unchecks the Per-Print checkbox, system sets the value to max.
			if (!value) {
				newFormData.perPrintValue = null;
			} else {
				newFormData.perPrintValue =
					!newFormData.perPrintValue ||
					newFormData.perPrintValue === Utils.MAXIMUM_LIMIT_AMOUNT
						? 0.0
						: newFormData.perPrintValue;
			}
		} else if (fieldName === 'perPrintValue' && value === 10000) {
			/**
			 * TO DO: WEBAPPS-23991 - Install Prime React 5.0.0-rc2 which fixes the issue of not being able to manually type 9999.99 into the wrapped input.
			 */
			newFormData.perPrintValue = 9999.99;
		}

		if (fieldName === 'role') {
			if (
				value === UserRole.LocationAdmin ||
				value === UserRole.AccountOwner ||
				value === UserRole.CorpAdmin ||
				value === UserRole.RegionAdmin
			) {
				newFormData.addFunds = true;
				newFormData.viewReports = true;
				newFormData.buySupplies = true;
				newFormData.costCodesRequired = false;
				newFormData.editCostCodes = true;
			} else if (
				value === UserRole.SingleUser ||
				value === UserRole.MultiUser
			) {
				newFormData.addFunds = false;
				newFormData.viewReports = false;
				newFormData.buySupplies = false;
				newFormData.costCodesRequired = false;
				newFormData.editCostCodes = false;
			}
		}

		this.setState(
			{
				formData: newFormData,
				clean: false,
			},
			() => {
				this.validateFormData(fieldName);
			}
		);
	};

	validateFormData = fieldName => {
		const { action } = this.props;
		const { userRole } = this.state;
		const formRule = Utils.getFormRule(action, userRole);
		const { errorMessage, formData } = this.state;
		const resultValidate = Utils.validateData(formRule, formData);
		this.setState({
			errorMessage: {
				...errorMessage,
				[fieldName]: resultValidate && resultValidate[fieldName],
			},
		});
	};

	handleResetPassword = async () => {
		const { selectedUsers } = this.state;
		const { modalContext } = this.props;
		const { UserId, UserName } = selectedUsers[0];
		const response = await AccountCommunication.resetPassword(UserId);
		if (AccountCommunication.isSuccessful(response)) {
			this.openResetPassWordModal(true);
			Logger.logToServer({
				category: 'Reset Password',
				forUsername: UserName,
			});
		} else {
			modalContext.showError({ response });
		}
	};

	sendResendEmail = async (userId, userName) => {
		const { modalContext } = this.props;
		const response = await AccountCommunication.sendActivationUrl(userId);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({ resendInviteStatus: 'resendEmail' });
			Logger.logToServer({
				category: 'Resend Invite Email',
				forUsernames: userName,
			});
		} else {
			modalContext.showError({ response });
		}
	};

	openResetPassWordModal = isResetPasswordComplete => {
		const { formData } = this.state;
		const { modalContext } = this.props;
		modalContext.showModal({
			component: ResetPasswordModal,
			data: {
				FullName: `${formData.firstName} ${formData.lastName}`,
				status: isResetPasswordComplete,
				EmailAddress: formData.email,
				callback: () => this.handleResetPassword(),
			},
		});
	};

	openResendInviteModal = async () => {
		const { formData } = this.state;
		const { modalContext, selectedUsers } = this.props;
		const { UserId, UserName } = selectedUsers[0];
		// call api
		const response = await AccountCommunication.getActivationUrl(UserId);

		if (AccountCommunication.isSuccessful(response)) {
			// clear the confirmation text if it's shown
			this.setState({
				resendInviteStatus: '',
			});

			modalContext.showModal({
				component: ResendInviteModal,
				data: {
					activationLink: response.data,
					FirstName: formData.firstName,
					EmailAddress: formData.email,
					userId: UserId,
					userName: UserName,

					// callback for clicking either button in the modal
					callback: resendInviteStatus => {
						if (resendInviteStatus === 'resendEmail') {
							// resend the email
							this.sendResendEmail(UserId, UserName);
						} else if (resendInviteStatus === 'copyLink') {
							// otherwise send log and,
							Logger.logToServer({
								category: 'Copy Invite Link',
								forUsernames: UserName,
							});

							// update the state to show "Link Copied to Clipboard"
							this.setState({
								resendInviteStatus,
							});
						}
					},
				},
			});
		} else {
			modalContext.showError({ response });
		}
	};

	getSingleRequestParams = () => {
		const { formData, userID, customerID, selectedUsers } = this.state;
		const { action, locations } = this.props;
		const capabilities = Utils.calculateUserCaps(formData);
		const userCapabilities = {
			Capabilities: capabilities,

			// Per WEBAPPS-25969, when the Per-Print checkbox is unchecked, system sets the max value for per-print
			PerPrintMaximumAmountLimit: formData.perPrint
				? formData.perPrintValue
				: Utils.MAXIMUM_LIMIT_AMOUNT,
		};

		const PhoneNumber = Util.removePhoneFormatting(formData.phone);
		let requestParams = { AdminUserId: userID };
		let user = {
			UserName: formData.userName,
			FirstName: formData.firstName,
			LastName: formData.lastName,
			EmailAddress: formData.email,
			PhoneNumber,
			UserCapabilities: userCapabilities,
		};
		if (action === UserActionType.Add) {
			const locationInfo =
				locations.find(item => item.MeterNo === formData.location) || {};
			const custId =
				Object.keys(locationInfo).length !== 0
					? locationInfo.CustId
					: customerID;
			user = { ...user, CustId: custId };
		} else {
			const { UserId, CustId } = selectedUsers[0] || {};
			user = { ...user, UserId, CustId };
		}
		if (this.isEnterpriseAccount) {
			const extraParams = this.additionalEnterpriseParams();
			requestParams = { ...requestParams, ...extraParams };
		}
		return { ...requestParams, User: user };
	};

	additionalEnterpriseParams = () => {
		const { formData } = this.state;
		const { locations, corpID } = this.props;
		const corpCapabilities = Utils.calculateCorpUserCaps(formData);
		let corpIdParam = '';
		if (formData.role === UserRole.CorpAdmin) {
			corpIdParam = corpID;
		} else {
			const locationInfo =
				locations.find(
					({ MeterNo, Name }) =>
						MeterNo === formData.location || Name === formData.location
				) || {};
			corpIdParam = locationInfo.CorpId;
		}
		return { CorpCapabilities: corpCapabilities, CorpId: corpIdParam };
	};

	addNewUser = async () => {
		const requestParams = this.getSingleRequestParams();
		let response = {};
		if (this.isEnterpriseAccount) {
			response = await AccountCommunication.addCorpUser(requestParams);
		} else {
			response = await AccountCommunication.addUser(requestParams);
		}
		return response;
	};

	handleSubmitFail = response => {
		const { onCloseUserDetails, modalContext } = this.props;
		this.setState({
			clean: false,
		});
		onCloseUserDetails();
		modalContext.showError({ response });
	};

	handleSubmitNewUserEdit = async () => {
		const { formData } = this.state;
		const { action, onCloseUserDetails, displayLoadingSpinner, modalContext } =
			this.props;

		// username must be null for new users
		if (action === UserActionType.Add && !this.isEnterpriseAccount) {
			formData.userName = null;
		}

		const response = await this.addNewUser();

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
			});
			await onCloseUserDetails();
			Logger.logToServer({
				category: 'Save New User',
				forUsername: formData.userName,
			});
		} else if (
			response.ErrorCode === 1000023 &&
			response.ErrorDescription === 'Number of users exceeds the limit.'
		) {
			const { maxSeats, history } = this.props;

			if (BillingPlans.isNewLineupPlan()) {
				modalContext.showModal({
					component: AddSeatsModal,
					data: {
						maxSeats,
						onNavigate: history?.push.bind(history),
					},
				});
			} else {
				modalContext.showModal({
					component: UserLimitReachedModal,
					data: {
						isEnterpriseAccount: this.isEnterpriseAccount,
					},
				});
			}
		} else if (response.ErrorCode === 1000024) {
			const suggestionUsername = response.ErrorDescription.replace(
				/['"]+/g,
				''
			);
			this.setState({
				formData: { ...formData, userName: suggestionUsername },
				errorMessage: {
					userName: [
						`The username already exists, we suggest ${suggestionUsername.bold()}`,
					],
				},
				clean: false,
			});
		} else {
			this.handleSubmitFail(response);
		}

		displayLoadingSpinner(false);
	};

	updateSingleUser = async () => {
		const requestParams = this.getSingleRequestParams();
		let response = {};
		if (this.isEnterpriseAccount) {
			response = await AccountCommunication.updateCorpUser(requestParams);
		} else {
			response = await AccountCommunication.updateUser(requestParams);
		}
		return response;
	};

	handleSubmitSingleUserEdit = async () => {
		const { fieldsChanged, formData } = this.state;
		const { displayLoadingSpinner, onCloseUserDetails } = this.props;
		const { userName } = formData;
		const response = await this.updateSingleUser();

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
			});
			await onCloseUserDetails();
			Logger.logToServer({
				category: 'Edit Single User',
				forUsername: userName,
				fieldsChanged: Utils.getFieldsChanged(formData, fieldsChanged),
			});
		} else {
			this.handleSubmitFail(response);
		}

		displayLoadingSpinner(false);
	};

	updateMultiUsers = async () => {
		const { formData, selectedUsers, userID, customerID } = this.state;
		const {
			displayLoadingSpinner,
			resetUserGrid,
			modalContext,
			onCloseUserDetails,
		} = this.props;
		const { perPrintValue, perPrint } = formData;
		const capabilitiesFields = [
			'addFunds',
			'viewReports',
			'buySupplies',
			'costCodesRequired',
			'editCostCodes',
		];
		const UserAndCapabilitiesPairs = selectedUsers.map(user => {
			const parsedFormData = Object.keys(formData).reduce((result, key) => {
				if (
					(key === 'role' || capabilitiesFields.includes(key)) &&
					formData[key] === ''
				) {
					const userField = `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
					result[key] = user[userField];
				} else {
					result[key] = formData[key];
				}
				return result;
			}, {});

			const UserCapabilities = {
				Capabilities: Utils.calculateUserCaps(parsedFormData),
			};

			if (perPrint === 'limit_to') {
				UserCapabilities.PerPrintMaximumAmountLimit = perPrintValue;
			} else if (perPrint === 'no_limit') {
				// per WEBAPPS-25969, set the max value for per-print
				UserCapabilities.PerPrintMaximumAmountLimit =
					Utils.MAXIMUM_LIMIT_AMOUNT;
			} else {
				UserCapabilities.PerPrintMaximumAmountLimit =
					user.UserCapabilities.PerPrintMaximumAmountLimit;
			}

			return {
				UserId: user.UserId,
				UserCapabilities,
			};
		});

		const UserCapabilitiesRequest = {
			CustId: customerID,
			AdminUserId: userID,
			UserAndCapabilitiesPairs,
		};

		const response = await AccountCommunication.updateMultipleUser(
			UserCapabilitiesRequest
		);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
			});
			await onCloseUserDetails();
			Logger.logToServer({
				category: 'Edit Multiple Users',
				forUsername: selectedUsers.map(user => user.UserName),
				fieldsChanged: Object.keys(formData).filter(
					key => formData[key] !== '' && formData[key] !== 0
				),
			});
		} else {
			this.setState({
				clean: false,
			});

			resetUserGrid();
			modalContext.showError({ response });
		}

		displayLoadingSpinner(false);
	};

	updateMultiCorpUsers = async () => {
		const { formData, selectedUsers, userID } = this.state;

		const {
			displayLoadingSpinner,
			resetUserGrid,
			onCloseUserDetails,
			modalContext,
			corpID,
		} = this.props;
		const { perPrintValue, role, perPrint } = formData;
		const capabilitiesFields = [
			'addFunds',
			'viewReports',
			'buySupplies',
			'costCodesRequired',
			'editCostCodes',
		];
		const multiUsersDoNotHaveRegion = selectedUsers.some(
			user => user.Region === ''
		);

		if (formData.role === UserRole.RegionAdmin && multiUsersDoNotHaveRegion) {
			return modalContext.showError({
				data: {
					title: 'Error',
					description: `Users who are not assigned to a region cannot be assigned to a region admin role.`,
					primaryButtonType: 'OK',
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}

		const CorporateUserAndCapabilitiesPairs = selectedUsers.map(user => {
			const { UsersCorporationInformation } = user;
			const parsedFormData = Object.keys(formData).reduce((result, key) => {
				if (
					(key === 'role' || capabilitiesFields.includes(key)) &&
					formData[key] === ''
				) {
					const userField = `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
					result[key] = user[userField];
				} else {
					result[key] = formData[key];
				}
				return result;
			}, {});

			const Capabilities = Utils.calculateUserCaps(parsedFormData);
			const CorpCapabilities = Utils.calculateCorpUserCaps(parsedFormData);
			const TargetCorpId =
				role === UserRole.CorpAdmin
					? corpID
					: UsersCorporationInformation.CorpId;
			const UserCapabilities = {
				Capabilities,
			};

			if (perPrint === 'limit_to') {
				UserCapabilities.PerPrintMaximumAmountLimit = perPrintValue;
			} else if (perPrint === 'no_limit') {
				// per WEBAPPS-25969, set the max value for per-print
				UserCapabilities.PerPrintMaximumAmountLimit =
					Utils.MAXIMUM_LIMIT_AMOUNT;
			} else {
				UserCapabilities.PerPrintMaximumAmountLimit =
					user.UserCapabilities.PerPrintMaximumAmountLimit;
			}
			return {
				CorporateUserData: {
					TargetUserId: UsersCorporationInformation.UserId,
					TargetCorpId,
					Status: 'string',
				},
				CorpCapabilities,
				UserCapabilities,
			};
		});

		const CorporateUsersCapabilitiesRequest = {
			AdminUserId: userID,
			CorporateUserAndCapabilitiesPairs,
		};
		const response = await AccountCommunication.updateMultipleCorpUser(
			CorporateUsersCapabilitiesRequest
		);
		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
			});
			await onCloseUserDetails();
			Logger.logToServer({
				category: 'Edit Multiple Users',
				forUsername: selectedUsers.map(user => user.UserName),
				fieldsChanged: Object.keys(formData).filter(
					key => formData[key] !== '' && formData[key] !== 0
				),
			});
		} else {
			this.setState({
				clean: false,
			});
			resetUserGrid();
			modalContext.showError({ response });
		}

		displayLoadingSpinner(false);
	};

	handleSubmitMultiUserEdit = async () => {
		if (this.isEnterpriseAccount) {
			this.updateMultiCorpUsers();
		} else {
			this.updateMultiUsers();
		}
	};

	handleSubmit = async () => {
		const { action, modalContext } = this.props;
		const { userRole, formData, selectedUsers } = this.state;
		const formRule = Utils.getFormRule(action, userRole);
		const validateResult = Utils.validateData(formRule, formData);

		this.setState({
			errorMessage: validateResult,
		});

		if (!isEmpty(validateResult)) {
			return;
		}

		if (action !== UserActionType.Add) {
			const originalUser = selectedUsers[0];
			const { isEmailChanged, isPhoneNumberChanged } =
				Util.checkContactChangesRequireVerification({
					oldPhoneNumber: originalUser.PhoneNumber,
					newPhoneNumber: Util.removePhoneFormatting(formData.phone),
					oldEmailAddress: originalUser.EmailAddress,
					newEmailAddress: formData.email,
				});

			if (isEmailChanged || isPhoneNumberChanged) {
				modalContext.showModal({
					component: AccountVerificationModal,
					data: {
						isChangingEmailAddress: isEmailChanged,
						completionCallback: this.closeAccountVerificationCallback,
					},
				});

				return;
			}
		}

		this.updateUserInfo();
	};

	closeAccountVerificationCallback = success => {
		const {
			modalContext: { showError },
		} = this.props;

		if (success) {
			this.updateUserInfo();
		} else {
			setTimeout(() => {
				showError({
					data: {
						title: 'Verification Required',
						description:
							'To change your phone number or email, you must verify your identity.',
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Close',
						handleType: 'custom',
					},
				});
			}, 0);
		}
	};

	updateUserInfo = () => {
		const { action, displayLoadingSpinner } = this.props;

		if (action === UserActionType.Add) {
			this.handleSubmitNewUserEdit();
		} else if (action === UserActionType.UpdateUser) {
			this.handleSubmitSingleUserEdit();
		} else if (action === UserActionType.UpdateMultiUser) {
			this.handleSubmitMultiUserEdit();
		}

		displayLoadingSpinner(true, 'Updating users...');
	};

	renderHeading = () => {
		const { action, selectedUsers } = this.props;
		let title = '';
		if (action === UserActionType.Add) {
			title = 'Add New User';
		} else if (action === UserActionType.UpdateUser) {
			title = `${selectedUsers[0].FirstName} ${selectedUsers[0].LastName}`;
		} else if (action === UserActionType.UpdateMultiUser) {
			title = `${selectedUsers.length} Users Selected`;
		}
		return <b>{title}</b>;
	};

	renderDetailsView = () => {
		const {
			action,
			locations,
			regions,
			enterpriseLocation,
			canManageAccountOwners,
		} = this.props;
		const { formData, errorMessage, disableAllField, selectedUsers } =
			this.state;
		const roles = Utils.getRoleDropDownByUser(action, selectedUsers, {
			canManageAccountOwners,
		});

		if (action === UserActionType.UpdateUser || action === UserActionType.Add) {
			// Determine if the selected user being edited is an Account Owner
			const selectedUser = selectedUsers[0];
			const isSelectedUserAccountOwner =
				action === UserActionType.UpdateUser &&
				selectedUser &&
				Utils.isUserAccountOwner(selectedUser);

			// Block edits to an Account Owner only when the current user has no
			// authority over Account Owners (i.e. is not Account Owner / Corp /
			// Region Admin).
			const isViewingOwnerWithoutAuthority =
				isSelectedUserAccountOwner && !canManageAccountOwners;

			return (
				<SingleUserEditView
					selectedUser={formData}
					roles={roles}
					enterpriseLocation={enterpriseLocation}
					errorMessage={errorMessage}
					handleFormInputChange={this.handleFormInputChange}
					openResendInviteModal={this.openResendInviteModal}
					openResetPassWordModal={this.openResetPassWordModal}
					action={action}
					validateFormData={this.validateFormData}
					locations={locations}
					regions={regions}
					disableAllField={disableAllField || isViewingOwnerWithoutAuthority}
					isAccountOwner={isSelectedUserAccountOwner}
					permissionsReadOnly={isViewingOwnerWithoutAuthority}
					canManageAccountOwners={canManageAccountOwners}
				/>
			);
		} else if (action === UserActionType.UpdateMultiUser) {
			return (
				<MultiUsersEditView
					multiUserState={formData}
					errorMessage={errorMessage}
					handleFormInputChange={this.handleFormInputChange}
					roles={roles}
					disableAllField={disableAllField}
				/>
			);
		}
		return null;
	};

	render() {
		const { resetUserGrid } = this.props;
		const { resendInviteStatus, clean, disableAllField } = this.state;
		return (
			<div className="account-users-user-details">
				<div className="user-details-heading">
					{this.renderHeading()}
					{!!resendInviteStatus && (
						<SaveConfirmation
							success
							text={
								resendInviteStatus === 'copyLink'
									? 'Link Copied to Clipboard'
									: 'Email Sent'
							}
						/>
					)}
				</div>
				<div className="user-details-container">{this.renderDetailsView()}</div>
				<div className="user-details-footer">
					<WrappedButton
						buttonType={WrappedButtonType.Secondary}
						label="Cancel"
						onClick={resetUserGrid}
						dataqa="cancel"
					/>
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						label="Save"
						onClick={this.handleSubmit}
						dataqa="save"
						disabled={clean || disableAllField}
					/>
				</div>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(UserDetails);
